import {
  flattenVaultMenuRouteKeys,
  normalizeVaultMenuRouteKey,
  VAULT_MENU_CANONICAL,
} from './vault-menu-canonical.snapshot';
import {
  readVaultDashboardRole,
  readVaultIsSuperadminScope,
  readVaultOperativeRole,
  isVaultOperativeScopeHydrated,
  resolveSuperadminFromOperativeScope,
  mapVaultTenantRoleToQaProfile,
  tokenImpliesVaultAdminRole,
} from '../vault-operative-role.util';
import {
  dashboardRoleIndicatesVaultSuperadmin,
  vaultUserIsSuperadminFromSession,
} from '../vault-user-roles.util';

export type VaultQaRoleProfile = 'admin' | 'auditor' | 'archivista' | 'consulta' | 'unknown';

const SUPERADMIN_TOKENS = new Set([
  'superadmin',
  'super_admin',
  'super_administrador',
  'administrador_global',
  'platform_admin',
]);

const ADMIN_TOKENS = new Set([
  ...SUPERADMIN_TOKENS,
  'admin',
  'admin_empresa',
  'administrador',
]);

function normalizeRoleToken(raw: string): string {
  return raw
    .trim()
    .toLowerCase()
    .replace(/^role_/, '')
    .replace(/[\s-]+/g, '_');
}

function isTruthySuperadminFlag(value: unknown): boolean {
  if (value === true || value === 1) return true;
  if (typeof value === 'string') {
    const s = value.toLowerCase().trim();
    return s === 'true' || s === '1' || s === 'yes';
  }
  return false;
}

function readUserProfileCookie(): Record<string, unknown> | null {
  if (typeof document === 'undefined') return null;
  const match = document.cookie.match(/(?:^|;\s*)user_profile=([^;]*)/);
  if (!match?.[1]) return null;
  try {
    const parsed: unknown = JSON.parse(decodeURIComponent(match[1]));
    return parsed && typeof parsed === 'object' ? (parsed as Record<string, unknown>) : null;
  } catch {
    return null;
  }
}

function collectRoleTokensFromStorage(): string[] {
  const tokens: string[] = [];
  if (typeof localStorage === 'undefined') return tokens;

  try {
    const raw = localStorage.getItem('user_data');
    if (raw) {
      const user = JSON.parse(raw) as {
        roles?: Array<{ name?: string; authority?: string }>;
        is_superadmin?: unknown;
      };
      if (
        isTruthySuperadminFlag(user.is_superadmin) ||
        inferSuperadminFromUserRecord(user as unknown as Record<string, unknown>)
      ) {
        tokens.push('superadmin');
      }
      for (const r of user.roles ?? []) {
        if (r.name) tokens.push(r.name);
        if (r.authority) tokens.push(r.authority);
      }
    }
    const profile = readUserProfileCookie();
    if (profile && isTruthySuperadminFlag(profile['is_superadmin'])) {
      tokens.push('superadmin');
    }
  } catch {
    /* ignore */
  }

  const token = localStorage.getItem('access_token');
  if (token) {
    try {
      const part = token.split('.')[1];
      if (part) {
        const padded = part.replace(/-/g, '+').replace(/_/g, '/');
        const pad = padded + '='.repeat((4 - (padded.length % 4)) % 4);
        const payload = JSON.parse(atob(pad)) as Record<string, unknown>;
        const user = (payload['User'] ?? payload['user']) as Record<string, unknown> | undefined;
        if (user && isTruthySuperadminFlag(user['is_superadmin'])) {
          tokens.push('superadmin');
        }
        if (
          isTruthySuperadminFlag(payload['is_superadmin']) ||
          isTruthySuperadminFlag(payload['isSuperAdmin'])
        ) {
          tokens.push('superadmin');
        }
        if (user && inferSuperadminFromUserRecord(user)) {
          tokens.push('superadmin');
        }
        if (Array.isArray(user?.['roles'])) {
          for (const r of user['roles'] as Array<{ name?: string; authority?: string }>) {
            if (r.name) tokens.push(r.name);
            if (r.authority) tokens.push(r.authority);
          }
        }
        const authorities = payload['authorities'];
        if (Array.isArray(authorities)) {
          for (const a of authorities) {
            if (typeof a === 'string') tokens.push(a);
          }
        }
      }
    } catch {
      /* ignore */
    }
  }

  return tokens.map(normalizeRoleToken).filter(Boolean);
}

export function resolveVaultQaRoleProfile(): VaultQaRoleProfile {
  const vaultScopeSuperadmin = readVaultIsSuperadminScope();
  if (vaultScopeSuperadmin === true) return 'admin';

  const vaultRole = readVaultOperativeRole();
  if (vaultRole) {
    return mapVaultTenantRoleToQaProfile(vaultRole);
  }

  const tokens = collectRoleTokensFromStorage();
  if (tokens.some((t) => tokenImpliesVaultAdminRole(t))) return 'admin';
  if (tokens.includes('auditor')) return 'auditor';
  if (tokens.includes('archivista')) return 'archivista';
  if (tokens.includes('consulta')) return 'consulta';

  if (vaultScopeSuperadmin === false) return 'consulta';

  return 'unknown';
}

/** Perfil para cap de rutas: solo datos Vault hidratados (sin inferir desde Simappe DEVELOPER). */
export function resolveVaultQaRoleProfileForRestrictions(): VaultQaRoleProfile {
  const fromOperative = resolveSuperadminFromOperativeScope();
  if (fromOperative === true) return 'admin';

  const vaultRole = readVaultOperativeRole();
  if (readVaultIsSuperadminScope() === false && vaultRole) {
    return mapVaultTenantRoleToQaProfile(vaultRole);
  }

  if (userIsSuperadminFromSession()) return 'admin';

  const dashboardRole = readVaultDashboardRole();
  if (dashboardRole) {
    return mapVaultTenantRoleToQaProfile(dashboardRole);
  }

  if (readVaultIsSuperadminScope() === false) return 'consulta';

  return 'unknown';
}

export function inferSuperadminFromUserRecord(u: Record<string, unknown>): boolean {
  if (isTruthySuperadminFlag(u['is_superadmin'])) return true;
  const roles = u['roles'];
  if (!Array.isArray(roles)) return false;
  for (const role of roles) {
    const rec = role && typeof role === 'object' ? (role as Record<string, unknown>) : {};
    for (const key of ['name', 'authority'] as const) {
      const t = normalizeRoleToken(String(rec[key] ?? ''));
      if (SUPERADMIN_TOKENS.has(t) || t.includes('superadmin') || t.includes('super_admin')) {
        return true;
      }
    }
  }
  return false;
}

function sessionHasExplicitSuperadminFlag(): boolean {
  if (typeof localStorage === 'undefined') return false;
  try {
    const raw = localStorage.getItem('user_data');
    if (raw) {
      const user = JSON.parse(raw) as Record<string, unknown>;
      if (isTruthySuperadminFlag(user['is_superadmin'])) return true;
    }
    const profile = readUserProfileCookie();
    if (profile && isTruthySuperadminFlag(profile['is_superadmin'])) return true;
  } catch {
    /* ignore */
  }
  const token = typeof localStorage !== 'undefined' ? localStorage.getItem('access_token') : null;
  if (token) {
    try {
      const part = token.split('.')[1];
      if (part) {
        const padded = part.replace(/-/g, '+').replace(/_/g, '/');
        const pad = padded + '='.repeat((4 - (padded.length % 4)) % 4);
        const payload = JSON.parse(atob(pad)) as Record<string, unknown>;
        const user = (payload['User'] ?? payload['user']) as Record<string, unknown> | undefined;
        if (user && isTruthySuperadminFlag(user['is_superadmin'])) return true;
        if (
          isTruthySuperadminFlag(payload['is_superadmin']) ||
          isTruthySuperadminFlag(payload['isSuperAdmin'])
        ) {
          return true;
        }
      }
    } catch {
      /* ignore */
    }
  }
  return false;
}

export function userIsSuperadminFromSession(): boolean {
  return vaultUserIsSuperadminFromSession();
}

/** Superadmin nunca debe ver cap ni bloqueos de módulo (alias explícito). */
export function userIsVaultSuperadminStrict(): boolean {
  return userIsSuperadminFromSession();
}

/** Solo capar rutas cuando Vault confirmó rol tenant y el usuario NO es superadmin. */
export function shouldStrictApplyVaultRoleRestrictions(): boolean {
  if (userIsSuperadminFromSession()) return false;
  const scope = readVaultIsSuperadminScope();
  if (scope === false) return true;
  return readVaultOperativeRole() !== null;
}

export function userIsAdminEmpresaFromSession(): boolean {
  if (userIsSuperadminFromSession()) return false;
  const vaultRole = readVaultOperativeRole();
  if (isVaultOperativeScopeHydrated() || vaultRole) {
    return vaultRole ? normalizeRoleToken(vaultRole) === 'admin_empresa' : false;
  }
  return collectRoleTokensFromStorage().some(
    (t) => t === 'admin_empresa' || t === 'adminempresa' || t === 'administrador_empresa',
  );
}

/** Solo superadmin y administrador de empresa pueden asignar archivos/carpetas. */
export function userCanAssignRepositoryFilesFromSession(): boolean {
  syncVaultSuperadminInStoredUserData();
  return userIsSuperadminFromSession() || userIsAdminEmpresaFromSession();
}

/** Persiste `is_superadmin` en user_data si la sesión ya es superadmin. */
export function syncVaultSuperadminInStoredUserData(): void {
  if (typeof localStorage === 'undefined' || !userIsSuperadminFromSession()) return;
  try {
    const raw = localStorage.getItem('user_data');
    if (!raw) return;
    const data = JSON.parse(raw) as Record<string, unknown>;
    if (isTruthySuperadminFlag(data['is_superadmin'])) return;
    data['is_superadmin'] = true;
    localStorage.setItem('user_data', JSON.stringify(data));
  } catch {
    /* ignore */
  }
}

/** Matriz QA S8: rutas permitidas cuando el API no devuelve menú. */
export function getQaFallbackAllowedRouteKeys(profile: VaultQaRoleProfile): Set<string> {
  const all = flattenVaultMenuRouteKeys(VAULT_MENU_CANONICAL);

  if (profile === 'admin') return new Set(all);

  const allow = (paths: string[]) =>
    new Set(paths.map((p) => normalizeVaultMenuRouteKey(p)).filter(Boolean));

  if (profile === 'auditor') {
    return allow([
      '/layout/vault/dashboard',
      '/layout/vault/repository-admin',
      '/layout/vault/preservation',
      '/layout/vault/retention',
      '/layout/vault/legal-hold',
      '/layout/vault/reports',
      '/layout/vault/audit',
      '/layout/vault/settings',
      '/layout/vault/settings/profile',
      '/layout/vault/preservation-messages',
    ]);
  }

  if (profile === 'archivista') {
    return allow([
      '/layout/vault/dashboard',
      '/layout/vault/vault/projects',
      '/layout/vault/repository-admin',
      '/layout/vault/retention',
      '/layout/vault/settings/profile',
    ]);
  }

  if (profile === 'consulta') {
    return allow([
      '/layout/vault/dashboard',
      '/layout/vault/repository-admin',
      '/layout/vault/reports',
      '/layout/vault/settings/profile',
    ]);
  }

  return new Set();
}
